import React from 'react';
import { grade_by_student_shared_question_answers_path } from '@/modules/routes';
import { ISubmission } from '@/components/interfaces';
import useGradesTableStore from '../../../store/useGradesTableStore';
import GradeCell from '.';
import useReadingGameExerciseResults from '../../../hooks/queries/exerciseResults/useReadingGameExerciseResults';
import useSectionNotebookExerciseResults from '../../../hooks/queries/exerciseResults/useSectionNotebookExerciseResults';
import useAssignmentQuestionAnswers from '../../../hooks/queries/questionAnswers/useAssignmentQuestionAnswers';
import { IExerciseResult, ILowScoresSettings } from '../../../types';
import { COLOR_LEGEND } from '../../../variables';
import { IAssignmentWithExtras } from '../../../hooks/queries/useSectionAssignments';
import useSubmissions from '../../../hooks/queries/useSubmissions';
import isAwaitingData from '../../../hooks/queries/isAwaitingData';
import { IAssignmentModel } from '@/components/interfaces/Assignment';

const getStatus = (
  assignment: IAssignmentWithExtras,
  questionAnswers: ReturnType<typeof useAssignmentQuestionAnswers>['data'],
  exerciseResults: IExerciseResult[],
  submissions: ISubmission[],
  lowScoresSettings: ILowScoresSettings,
): { color: string; text: string } => {
  let text = '-';
  let totalResultsCompleted = 0;
  const models = Object.values(assignment.assignment_models_hash).flat();
  const submission = submissions.find(submission => submission.assignment_id === assignment.id);

  if (submission?.total_score_override) {
    const isLowScore = lowScoresSettings.enabled
      && submission.total_score_override <= (assignment.max_score * lowScoresSettings.threshold / 100);

    return {
      color: isLowScore ? COLOR_LEGEND.low_score : '',
      text: submission.total_score_override.toString(),
    };
  }

  exerciseResults.forEach((result) => {
    models.forEach((model) => {
      if (result.exercise_type !== model.assignment_type && result.exercise_id !== model.id) return;

      totalResultsCompleted += 1;
    });
  });

  const sectionNotebooksIsInProgress = () => {
    const sectionNotebookModelsCount = assignment?.assignment_models_hash?.Notebook?.length;
    const notebookExerciseResultCount = exerciseResults.filter(result => result.exercise_type === 'Notebook' && !!result.completed_at).length;
    return totalResultsCompleted > 0 && notebookExerciseResultCount < sectionNotebookModelsCount;
  };

  // Show In Progress if there are section notebooks that have not been turned in
  if (sectionNotebooksIsInProgress()) {
    text = 'In Progress';
    return { color: COLOR_LEGEND.in_progress, text };
  }

  if (totalResultsCompleted >= models.filter(m => m.assignment_type !== 'Section').length && totalResultsCompleted > 0) {
    const score = submission?.normalized_total_score ?? submission?.total_score;

    text = score ? score.toString() : 'Turned In';

    return { color: COLOR_LEGEND.completed, text };
  }

  const { question_ids: questionIds } = assignment;

  if (questionAnswers.some(qa => questionIds.includes(qa.question_id))) {
    return { color: COLOR_LEGEND.in_progress, text: 'In Progress' };
  }

  return { color: '', text: '-' };
};

interface AssignmentGradeCellProps {
  assignment: IAssignmentWithExtras;
  studentId: number;
}

const AssignmentGradeCell = ({ assignment, studentId }: AssignmentGradeCellProps) => {
  const programId = useGradesTableStore(state => state.programId);
  const classroom = useGradesTableStore(state => state.classroom);
  const lesson = useGradesTableStore(state => state.lesson);
  const lowScoresSettings = useGradesTableStore(state => state.lowScoresSettings);

  const readingGameResultsQuery = useReadingGameExerciseResults();
  const { data: readingGameResults } = readingGameResultsQuery;
  const sectionNotebookResultsQuery = useSectionNotebookExerciseResults();
  const { data: sectionNotebookExerciseResults } = sectionNotebookResultsQuery;
  const questionAnswersQuery = useAssignmentQuestionAnswers();
  const { data: questionAnswers } = questionAnswersQuery;
  const submissionsQuery = useSubmissions();
  const { data: submissions } = submissionsQuery;

  const isLoading = isAwaitingData(
    readingGameResultsQuery,
    sectionNotebookResultsQuery,
    questionAnswersQuery,
    submissionsQuery,
  );

  const getAssigned = (type: string): IAssignmentModel[] => assignment?.assignment_models_hash?.[type] || [];
  const hasAssigned = type => getAssigned(type).length > 0;

  const assignedNotebook = getAssigned('Notebook');
  const assignedSection = getAssigned('Section');

  const assignedNotebookIds = assignedNotebook.map(notebook => notebook.id);

  const firstSection = assignedSection[0];

  // Exercise Results for Reading "Text" assignments
  const sectionExerciseResultsForAssignment = hasAssigned('Section')
    ? sectionNotebookExerciseResults?.filter(
      result => result.exercise_type === 'Section'
        && result.exercise_id === firstSection.id
        && result.student_id === studentId,
    ) : [];

  // Exercise Results for Reading "Notes" assignments
  const notebookExerciseResultsForAssignment = hasAssigned('Notebook')
    ? sectionNotebookExerciseResults?.filter(
      result => result.exercise_type === 'Notebook'
        && assignedNotebookIds.includes(result.exercise_id)
        && result.student_id === studentId,
    ) : [];

  // Exercise Results for Reading "Check" assignments
  const readingGameResultsForAssignment = hasAssigned('ReadingGame')
    ? readingGameResults?.filter(
      result => result.assignments?.some(item => item.id === assignment.id)
      && result.student_id === studentId,
    ) : [];

  const exerciseResultsForAssignment = () => [
    ...readingGameResultsForAssignment,
    ...notebookExerciseResultsForAssignment,
    ...sectionExerciseResultsForAssignment,
  ];

  const status = getStatus(
    assignment,
    questionAnswers.filter(qa => qa.student_id === studentId),
    exerciseResultsForAssignment(),
    submissions.filter(submission => submission.user_id === studentId && submission.user_type === 'Student'),
    lowScoresSettings,
  );

  const url = grade_by_student_shared_question_answers_path(
    studentId,
    { assignment_id: assignment.id, classroom_id: classroom.id, lesson_id: lesson.id, program_id: programId },
  );

  return (
    <GradeCell
      id={`assignment-${assignment.id}-student-score`}
      isLoading={isLoading}
      status={status}
      url={url}
    />
  );
};

export default AssignmentGradeCell;
